import React, { useState, useEffect, useCallback, memo } from "react";
import styled from "styled-components";
import {
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
  WarningOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { getLeadFlowProgress } from "../services/leadFlowProgressService.js";
import UserDetailsModal from "./UserDetailsModal.jsx";

/**
 * Lead Flow Progress Widget
 * Shows real-time tracking of users currently in the lead flow
 */
const LeadFlowProgressWidget = ({
  showControls = true,
  refreshInterval = 30000, // 30 seconds
  onDataUpdate = null,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    fetchData();

    // Set up auto-refresh
    const interval = setInterval(fetchData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const fetchData = useCallback(
    async (isManualRefresh = false) => {
      // Only show loading spinner for manual refreshes, not auto-refreshes
      if (isManualRefresh) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await getLeadFlowProgress();

        if (response.success) {
          setData(response.data);
          if (onDataUpdate) {
            onDataUpdate(response.data);
          }
        } else {
          setError(response.error || "Failed to fetch data");
        }
      } catch (err) {
        setError(err.message || "Network error");
      } finally {
        if (isManualRefresh) {
          setLoading(false);
        }
      }
    },
    [onDataUpdate]
  );

  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStepColor = (stepName) => {
    if (stepName.includes("Introduction") || stepName.includes("Collecting")) {
      return "#1890ff";
    }
    if (stepName.includes("Confirmation") || stepName.includes("Creation")) {
      return "#52c41a";
    }
    if (stepName.includes("Edit") || stepName.includes("Status")) {
      return "#faad14";
    }
    return "#722ed1";
  };

  const handleViewUsers = useCallback((stepName, stepData) => {
    setSelectedStep({ stepName, stepData });
    setShowUserModal(true);
  }, []);

  const handleCloseUserModal = useCallback(() => {
    setShowUserModal(false);
    setSelectedStep(null);
  }, []);

  if (loading && !data) {
    return (
      <WidgetContainer>
        <WidgetHeader>
          <WidgetTitle>Lead Flow Progress</WidgetTitle>
          <ReloadOutlined spin />
        </WidgetHeader>
        <LoadingMessage>Loading lead flow data...</LoadingMessage>
      </WidgetContainer>
    );
  }

  if (error) {
    return (
      <WidgetContainer>
        <WidgetHeader>
          <WidgetTitle>Lead Flow Progress</WidgetTitle>
          <ReloadButton onClick={() => fetchData(true)} disabled={loading}>
            <ReloadOutlined spin={loading} />
          </ReloadButton>
        </WidgetHeader>
        <ErrorMessage>
          <ExclamationCircleOutlined />
          <span>{error}</span>
        </ErrorMessage>
      </WidgetContainer>
    );
  }

  if (!data || data.totalActiveUsers === 0) {
    return (
      <WidgetContainer>
        <WidgetHeader>
          <WidgetTitle>Lead Flow Progress</WidgetTitle>
          {showControls && (
            <ReloadButton onClick={() => fetchData(true)} disabled={loading}>
              <ReloadOutlined spin={loading} />
            </ReloadButton>
          )}
        </WidgetHeader>
        <NoDataMessage>
          <UserOutlined />
          <span>No users currently in lead flow</span>
        </NoDataMessage>
      </WidgetContainer>
    );
  }

  const { stepDistribution, completionMetrics, stuckUsers, engagementMetrics } =
    data;
  const sortedSteps = Object.entries(stepDistribution).sort(
    ([, a], [, b]) => b.count - a.count
  );

  return (
    <WidgetContainer>
      <WidgetHeader>
        <WidgetTitle>Lead Flow Progress</WidgetTitle>
        {showControls && (
          <WidgetControls>
            <LastUpdated>
              Updated {new Date(data.lastUpdated).toLocaleTimeString()}
            </LastUpdated>
            <ReloadButton onClick={() => fetchData(true)} disabled={loading}>
              <ReloadOutlined spin={loading} />
            </ReloadButton>
          </WidgetControls>
        )}
      </WidgetHeader>

      {/* Summary Stats */}
      <StatsGrid>
        <StatCard>
          <StatIcon style={{ color: "#1890ff" }}>
            <UserOutlined />
          </StatIcon>
          <StatContent>
            <StatNumber>{data.totalActiveUsers}</StatNumber>
            <StatLabel>Active Users</StatLabel>
            <StatStatus>In Lead Flow</StatStatus>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon style={{ color: "#52c41a" }}>
            <CheckCircleOutlined />
          </StatIcon>
          <StatContent>
            <StatNumber>{completionMetrics.completionRate}%</StatNumber>
            <StatLabel>Completion Rate</StatLabel>
            <StatStatus>Success Rate</StatStatus>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon style={{ color: "#faad14" }}>
            <ClockCircleOutlined />
          </StatIcon>
          <StatContent>
            <StatNumber>
              {formatTime(engagementMetrics.averageTimeInFlow)}
            </StatNumber>
            <StatLabel>Avg. Time</StatLabel>
            <StatStatus>In Flow</StatStatus>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon style={{ color: "#ff4d4f" }}>
            <WarningOutlined />
          </StatIcon>
          <StatContent>
            <StatNumber>{stuckUsers.length}</StatNumber>
            <StatLabel>Stuck Users</StatLabel>
            <StatStatus>10+ min</StatStatus>
          </StatContent>
        </StatCard>
      </StatsGrid>

      {/* Step Distribution */}
      <Section>
        <SectionTitle>Current Step Distribution</SectionTitle>
        <StepsList>
          {sortedSteps.map(([stepName, stepData]) => (
            <StepItem key={stepName}>
              <StepIndicator color={getStepColor(stepName)}>
                <StepNumber>{stepData.count}</StepNumber>
              </StepIndicator>
              <StepInfo>
                <StepName>{stepName}</StepName>
                <StepDetails>
                  {stepData.count} users • Avg.{" "}
                  {formatTime(stepData.averageTimeInStep)}
                </StepDetails>
              </StepInfo>
              <StepActions>
                <ViewUsersButton
                  onClick={() => handleViewUsers(stepName, stepData)}
                  title={`View ${stepData.count} users in ${stepName}`}
                >
                  <EyeOutlined />
                  View Users
                </ViewUsersButton>
              </StepActions>
              <StepProgress>
                <ProgressBar>
                  <ProgressFill
                    width={(stepData.count / data.totalActiveUsers) * 100}
                    color={getStepColor(stepName)}
                  />
                </ProgressBar>
                <ProgressPercent>
                  {Math.round((stepData.count / data.totalActiveUsers) * 100)}%
                </ProgressPercent>
              </StepProgress>
            </StepItem>
          ))}
        </StepsList>
      </Section>

      {/* Stuck Users Alert */}
      {stuckUsers.length > 0 && (
        <Section>
          <SectionTitle>
            <WarningOutlined style={{ color: "#ff4d4f", marginRight: "8px" }} />
            Users Needing Attention
          </SectionTitle>
          <StuckUsersList>
            {stuckUsers.slice(0, 5).map((user, index) => (
              <StuckUserItem key={index}>
                <StuckUserInfo>
                  <StuckUserPhone>{user.phone}</StuckUserPhone>
                  <StuckUserStep>{user.stepName}</StuckUserStep>
                </StuckUserInfo>
                <StuckUserTime>
                  Stuck {formatTime(user.stuckDuration)}
                </StuckUserTime>
              </StuckUserItem>
            ))}
            {stuckUsers.length > 5 && (
              <StuckUserMore>
                +{stuckUsers.length - 5} more users need attention
              </StuckUserMore>
            )}
          </StuckUsersList>
        </Section>
      )}

      {/* User Details Modal */}
      <UserDetailsModal
        key={`modal-${selectedStep?.stepName || "default"}-${selectedStep?.stepData?.users?.length || 0}`}
        visible={showUserModal}
        onClose={handleCloseUserModal}
        stepName={selectedStep?.stepName}
        stepData={selectedStep?.stepData}
      />
    </WidgetContainer>
  );
};

// Styled Components
const WidgetContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #f0f0f0;
`;

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const WidgetTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #262626;
`;

const WidgetControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LastUpdated = styled.span`
  font-size: 12px;
  color: #8c8c8c;
`;

const ReloadButton = styled.button`
  background: none;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #595959;

  &:hover:not(:disabled) {
    border-color: #1890ff;
    color: #1890ff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #8c8c8c;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  color: #ff4d4f;
  font-size: 14px;
`;

const NoDataMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 20px;
  color: #8c8c8c;
  font-size: 14px;

  svg {
    font-size: 32px;
    color: #d9d9d9;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`;

const StatIcon = styled.div`
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #262626;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #595959;
  margin-top: 4px;
`;

const StatStatus = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 2px;
`;

const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  display: flex;
  align-items: center;
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`;

const StepIndicator = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const StepNumber = styled.span`
  color: white;
`;

const StepInfo = styled.div`
  flex: 1;
`;

const StepActions = styled.div`
  display: flex;
  align-items: center;
  margin-right: 12px;
`;

const ViewUsersButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #f0f0f0;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  color: #595959;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e6f7ff;
    border-color: #1890ff;
    color: #1890ff;
  }

  svg {
    font-size: 12px;
  }
`;

const StepName = styled.div`
  font-weight: 500;
  color: #262626;
  margin-bottom: 2px;
`;

const StepDetails = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const StepProgress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 80px;
`;

const ProgressBar = styled.div`
  width: 60px;
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.width}%;
  background: ${(props) => props.color};
  transition: width 0.3s ease;
`;

const ProgressPercent = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 500;
`;

const StuckUsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StuckUserItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
`;

const StuckUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StuckUserPhone = styled.div`
  font-weight: 500;
  color: #262626;
  font-size: 14px;
`;

const StuckUserStep = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const StuckUserTime = styled.div`
  font-size: 12px;
  color: #ff4d4f;
  font-weight: 500;
`;

const StuckUserMore = styled.div`
  text-align: center;
  padding: 8px;
  font-size: 12px;
  color: #8c8c8c;
  font-style: italic;
`;

export default memo(LeadFlowProgressWidget);
